import { useState } from 'react'
import { WalletCards } from 'lucide-react'
import type { ReactNode } from 'react'
import { Card, EmptyState, ErrorState, Skeleton } from '@shared/design-system/ui'
import { useWalletBalanceQuery } from '@entities/wallet'
import type { StatementEntry } from '@entities/wallet'
import type { Currency } from '@shared/contracts/enums'
import { useWalletStatementInfiniteQuery } from '@entities/wallet/api/wallet.queries'
import type { StatementFilters } from '@entities/wallet/api/wallet.queries'
import { analyticsDateRange } from '@shared/lib/analytics-period'
import { BalanceCards } from './BalanceCards'
import { LedgerEntryDrawer } from './LedgerEntryDrawer'
import { ReleaseDetailDrawer } from './ReleaseDetailDrawer'
import { StatementList } from './StatementList'
import { ExportButton } from '@shared/ui/ExportButton'
import type { ExportFormat } from '@shared/ui/ExportButton'
import { apiFetchBlob } from '@shared/api/http'
import { exportTimestamp } from '@shared/lib/export-timestamp'
import { triggerDownload } from '@shared/lib/download'
import { StatementToolbar } from './StatementToolbar'
import type { StatementSearch } from './StatementToolbar'
import { AutomaticPayouts } from './AutomaticPayouts'
import { UpcomingReleases, UpcomingReleasesDrawer } from './UpcomingReleases'
import type { SelectedRelease } from './UpcomingReleases'
import {
  contentGrid,
  header,
  headerText,
  helpText,
  page,
  sectionHeader,
  sectionTitle,
  subtitle,
  title,
} from './wallet.css'

const DEFAULT_PRESET = '30d' as const

function statementFiltersFromSearch(
  search: StatementSearch,
  currency?: Currency,
): StatementFilters {
  const range = analyticsDateRange({
    preset: search.preset ?? DEFAULT_PRESET,
    from: search.from ?? null,
    to: search.to ?? null,
  })
  const filters: StatementFilters = { direction: search.direction }
  if (currency) {
    filters.currency = currency
  }
  if (search.q) {
    filters.q = search.q
  }
  if (range.dateFrom) {
    filters.dateFrom = range.dateFrom
  }
  if (range.dateTo) {
    filters.dateTo = range.dateTo
  }
  return filters
}

type WalletScreenProps = {
  onViewSale?: (saleId: string) => void
  accountNotice?: ReactNode
  search?: StatementSearch
  onSearchChange?: (next: StatementSearch) => void
  currency?: Currency
  onCurrencyChange?: (currency: Currency) => void
}

export function WalletScreen({
  onViewSale,
  accountNotice,
  search = {},
  onSearchChange = () => {},
}: WalletScreenProps = {}) {
  const balance = useWalletBalanceQuery()
  const [selectedEntry, setSelectedEntry] = useState<StatementEntry | null>(null)
  const [selectedRelease, setSelectedRelease] = useState<SelectedRelease | null>(null)
  const [releaseScheduleOpen, setReleaseScheduleOpen] = useState(false)

  const loading = balance.isPending && !balance.data
  const hasError = balance.isError
  const balances = balance.data ?? []
  const activeCurrency = 'BRL'
  const visibleBalances = activeCurrency
    ? balances.filter((walletBalance) => walletBalance.currency === activeCurrency)
    : balances
  const statementFilters = statementFiltersFromSearch(search, activeCurrency)
  const statementEnabled = activeCurrency !== undefined
  const statement = useWalletStatementInfiniteQuery(statementFilters, statementEnabled)
  const statementPages = statement.data?.pages ?? []
  const statementEntries = statementPages.flatMap((page) => page.items)

  async function handleExport(format: ExportFormat) {
    const qs = new URLSearchParams({ format })
    if (statementFilters.currency) qs.set('currency', statementFilters.currency)
    if (statementFilters.dateFrom) qs.set('dateFrom', statementFilters.dateFrom)
    if (statementFilters.dateTo) qs.set('dateTo', statementFilters.dateTo)
    if (statementFilters.q) qs.set('q', statementFilters.q)
    if (statementFilters.direction) qs.set('direction', statementFilters.direction)
    const blob = await apiFetchBlob(`/wallet/statement/export?${qs}`)
    triggerDownload(blob, `extrato-${exportTimestamp()}.${format}`)
  }

  function loadMoreStatement() {
    if (statement.hasNextPage && !statement.isFetchingNextPage) {
      void statement.fetchNextPage()
    }
  }

  const statementFiltered =
    Boolean(search.q) ||
    Boolean(search.direction) ||
    (search.preset !== undefined && search.preset !== DEFAULT_PRESET) ||
    Boolean(search.from) ||
    Boolean(search.to)

  function retry() {
    void balance.refetch()
    void statement.refetch()
  }

  return (
    <div className={page} data-testid="wallet-screen">
      <div className={header}>
        <div className={headerText}>
          <span className={title}>Recebimentos</span>
          <span className={subtitle}>Seus saldos atuais, liberações e extrato em reais.</span>
        </div>
        <ExportButton onExport={handleExport} disabled={!statementEnabled} />
      </div>

      {accountNotice}
      <p className={helpText}>
        As liberações do saldo seguem os registros da carteira. Elas não comprovam depósito
        bancário; os repasses automáticos são acompanhados separadamente abaixo.
      </p>

      {loading ? (
        <div>
          <Skeleton height={190} />
        </div>
      ) : hasError && !balance.data ? (
        <ErrorState
          title="Não foi possível consultar o saldo"
          description="Atualize a consulta para tentar novamente."
          onRetry={retry}
        />
      ) : balances.length === 0 ? (
        <EmptyState
          icon={<WalletCards size={22} />}
          title="Sua carteira aparecerá após a primeira venda"
          description="Quando houver valores a receber, seus saldos ficam disponíveis aqui."
        />
      ) : (
        <>
          {hasError ? (
            <ErrorState
              title="Não foi possível atualizar o saldo"
              description="Os dados anteriores continuam visíveis. Tente novamente."
              onRetry={retry}
            />
          ) : null}
          <BalanceCards balances={visibleBalances} />

          <UpcomingReleases
            balances={visibleBalances}
            onOpen={() => setReleaseScheduleOpen(true)}
          />

          <div className={contentGrid}>
            <Card padding="md">
              <div className={sectionHeader}>
                <span className={sectionTitle}>Extrato</span>
              </div>
              <span className={helpText}>
                Créditos e débitos da sua conta, em ordem de acontecimento.
              </span>
              <StatementToolbar
                search={search}
                onChange={onSearchChange}
                resultCount={statementEntries.length}
              />
              {statement.isError && statement.data ? (
                <ErrorState
                  title="Não foi possível atualizar o extrato"
                  description="Os movimentos anteriores continuam visíveis. Tente novamente."
                  onRetry={() => {
                    void statement.refetch()
                  }}
                />
              ) : null}
              {statement.isPending ? (
                <div>
                  <Skeleton height={64} />
                  <Skeleton height={64} />
                  <Skeleton height={64} />
                </div>
              ) : statement.isError && !statement.data ? (
                <ErrorState
                  onRetry={() => {
                    void statement.refetch()
                  }}
                />
              ) : (
                <StatementList
                  entries={statementEntries}
                  onSelect={setSelectedEntry}
                  filtered={statementFiltered}
                  hasMore={Boolean(statement.hasNextPage)}
                  loadingMore={statement.isFetchingNextPage}
                  onLoadMore={loadMoreStatement}
                />
              )}
            </Card>
          </div>
        </>
      )}

      <AutomaticPayouts onViewSale={onViewSale} />

      <LedgerEntryDrawer
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
        onViewSale={(saleId) => {
          setSelectedEntry(null)
          onViewSale?.(saleId)
        }}
      />

      <UpcomingReleasesDrawer
        balances={visibleBalances}
        open={releaseScheduleOpen}
        onClose={() => setReleaseScheduleOpen(false)}
        onSelect={(release) => {
          setReleaseScheduleOpen(false)
          setSelectedRelease(release)
        }}
      />

      <ReleaseDetailDrawer
        release={selectedRelease}
        onClose={() => setSelectedRelease(null)}
        onBack={() => {
          setSelectedRelease(null)
          setReleaseScheduleOpen(true)
        }}
        onViewSale={(saleId) => {
          setSelectedRelease(null)
          onViewSale?.(saleId)
        }}
      />
    </div>
  )
}
